import { createContext, ReactNode, useCallback, useContext, useMemo, useSyncExternalStore } from 'react';

type SelectorType<Store, T> = {
  selector?: string | string[];
  observe?: boolean;  // When false, won't subscribe to updates
};

type UpdateFunction<T> = (prev: T) => T;
export type SetStateAction<T extends Record<string, any>, K extends keyof T = keyof T> = 
  | T 
  | Partial<{ [P in K]: T[P] | UpdateFunction<T[P]> }>;

export function createStore<Store extends Record<string, any>>(initialData: Store) {
  const storeData = {
    current: initialData
  };

  type StoreType = {
    subscribers: Set<() => void>;
    get(): Store;
    set(value: Partial<Store>, overwrite?: boolean): void;
    subscribe(callback: () => void): () => void;
    notify(): void;
  };

  const store: StoreType = {
    subscribers: new Set<() => void>(),
    
    get(): Store {
      return storeData.current;
    },

    set(value: Partial<Store>, overwrite = false) {
      const currentData = this.get();
      const newData = overwrite ? value : { ...currentData };

      if (!overwrite) {
        Object.entries(value).forEach(([key, value]) => {
          const k = key as keyof Store;
          newData[k] = value as Store[keyof Store];
        });
      }

      storeData.current = newData as Store;
      this.notify();
    },

    subscribe(callback: () => void) {
      this.subscribers.add(callback);
      return () => this.subscribers.delete(callback);
    },

    notify() {
      this.subscribers.forEach(callback => callback());
    }
  };

  const StoreContext = createContext<StoreType | null>(null);

  function StoreProvider({ children }: { children: ReactNode }) {
    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
  }

  function useSingleSelector<T extends Record<string, any>>(store: StoreType, selector: string, observe = true) {
    const getSnapshot = useCallback(() => {
      const data = store.get();
      return data[selector] as T;
    }, [selector]);

    const subscribe = useCallback((callback: () => void) => {
      return observe ? store.subscribe(callback) : () => {};
    }, [observe]);

    const state = useSyncExternalStore(subscribe, getSnapshot);

    const setState = useCallback((valueOrFn: SetStateAction<T>, overwrite?: boolean) => {
      const currentValue = store.get()[selector] as T;
      const newValue = typeof valueOrFn === 'function' 
        ? (valueOrFn as UpdateFunction<T>)(currentValue)
        : valueOrFn;
      
      store.set({ [selector]: newValue } as Partial<Store>, overwrite);
    }, [selector]);

    return [state, setState] as const;
  }

  function useStore<T extends Record<string, any>>(options: SelectorType<Store, T> = {}) {
    const store = useContext(StoreContext);
    if (!store) throw new Error('Store not found');

    const { selector, observe = true } = options;

    if (!selector) {
      const getSnapshot = useCallback(() => store.get() as unknown as T, []);
      const subscribe = useCallback((cb: () => void) => {
        return observe ? store.subscribe(cb) : () => {};
      }, [observe]);
      const state = useSyncExternalStore(subscribe, getSnapshot);
      const setState = useCallback((valueOrFn: SetStateAction<T>, overwrite?: boolean) => {
        const currentState = store.get() as unknown as T;
        const newValue = typeof valueOrFn === 'function'
          ? (valueOrFn as UpdateFunction<T>)(currentState)
          : valueOrFn;
        
        store.set(newValue as unknown as Partial<Store>, overwrite);
      }, []);
      return [state, setState] as const;
    }

    if (Array.isArray(selector)) {
      const results = selector.map(key => useSingleSelector<any>(store, key, observe));
      
      const state = Object.fromEntries(
        selector.map((key, index) => [key, results[index][0]])
      ) as T;

      const setState = useCallback((valueOrFn: SetStateAction<T>, overwrite?: boolean) => {
        if (typeof valueOrFn === 'function') {
          const newValue = (valueOrFn as UpdateFunction<T>)(state);
          store.set(newValue as Partial<Store>, overwrite);
          return;
        }

        const currentData = store.get();
        const updates = Object.entries(valueOrFn).reduce((acc, [key, value]) => {
          if (typeof value === 'function') {
            acc[key] = value(currentData[key]);
          } else {
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, any>);

        store.set(updates as Partial<Store>, overwrite);
      }, [state]);

      return [state, setState] as const;
    }

    return useSingleSelector<T>(store, selector, observe);
  }

  return [StoreProvider, useStore] as const;
}
